import { useState, useEffect } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import { Save, ChevronDown, Building2, Mail, Palette, Settings as SettingsIcon, Info, Globe, AlertTriangle, Lock, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import DomainSetupModal from '../components/onboarding/DomainSetupModal';
import MediaUploader from '../components/UI/MediaUploader';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Available languages ───────────────────────────────────────────────────────
const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
];

// ─── Language toggle pill ──────────────────────────────────────────────────────
function LangPill({ lang, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(lang.code)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all"
      style={{
        backgroundColor: selected ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
        borderColor: selected ? 'var(--accent-primary)' : 'var(--border-color)',
        color: selected ? '#fff' : 'var(--text-secondary)',
      }}
    >
      <span>{lang.flag}</span>
      <span>{lang.label}</span>
    </button>
  );
}

// ─── Mobile collapsible section ───────────────────────────────────────────────
function MobileSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [expanded, setExpanded] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--accent-primary)20', color: 'var(--accent-primary)' }}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="flex-1 text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </span>
        <ChevronDown className="w-4 h-4 transition-transform duration-200"
          style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
      </button>
      {expanded && (
        <div className="border-t px-4 py-4 space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Mobile input field ────────────────────────────────────────────────────────
function MobileField({ label, children, required }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wide mb-1.5"
        style={{ color: 'var(--text-secondary)' }}>
        {label} {required && <span style={{ color: 'var(--accent-danger)' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label, description, danger }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold" style={{ color: danger ? 'var(--accent-danger)' : 'var(--text-primary)' }}>{label}</p>
        {description && <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none"
        style={{ backgroundColor: checked ? (danger ? 'var(--accent-danger)' : 'var(--accent-primary)') : 'var(--bg-tertiary)', border: '2px solid var(--border-color)' }}
      >
        <span
          className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
          style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  );
}

export default function Settings() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const { fetchBrokerSettings, updateBrokerSettings } = useBroker();

  const [brokerSettings, setBrokerSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDomainHelp, setShowDomainHelp] = useState(false);
  const [domainLocked, setDomainLocked] = useState(true);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await fetchBrokerSettings();
      const data = response.data;
      // Normalize supported_languages to always be an array
      if (data && !Array.isArray(data.supported_languages)) {
        data.supported_languages = data.supported_languages ? [data.supported_languages] : ['en'];
      }
      setBrokerSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBrokerSettings = async () => {
    setSaving(true);
    try {
      await updateBrokerSettings(brokerSettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleLanguage = (code) => {
    const current = brokerSettings.supported_languages || ['en'];
    const next = current.includes(code)
      ? current.filter(l => l !== code).length === 0 ? current : current.filter(l => l !== code) // always keep at least 1
      : [...current, code];
    setBrokerSettings({ ...brokerSettings, supported_languages: next });
  };

  // Common input styles
  const inputClass = "w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none";
  const inputStyle = { backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' };
  const readonlyStyle = { ...inputStyle, opacity: 0.6, cursor: 'not-allowed' };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Broker Settings
          </p>
          <p className="text-2xl font-black text-white leading-tight">
            {brokerSettings?.name || 'Loading...'}
          </p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Configure your broker
          </p>
        </div>

        {/* ── Loading ─────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : brokerSettings ? (
          <>
            <div className="px-4 pt-3 pb-8 space-y-3">

              {/* General Information */}
              <MobileSection title="General Information" icon={Building2} defaultOpen={true}>
                <MobileField label="Broker Name" required>
                  <input type="text" value={brokerSettings.name || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, name: e.target.value })}
                    className={inputClass} style={inputStyle} placeholder="My Broker" required />
                </MobileField>

                <MobileField label={
                  <div className="flex items-center justify-between">
                    <span>Domain</span>
                    <button type="button" onClick={() => setShowDomainHelp(true)}
                      className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide"
                      style={{ color: 'var(--accent-primary)' }}>
                      <Info className="w-3 h-3" /> How to connect
                    </button>
                  </div>
                }>
                  <div className="relative">
                    <input
                      type="text"
                      value={brokerSettings.domain || ''}
                      onChange={e => setBrokerSettings({ ...brokerSettings, domain: e.target.value })}
                      readOnly={domainLocked}
                      className={inputClass}
                      style={{ ...inputStyle, paddingRight: '2.75rem', opacity: domainLocked ? 0.65 : 1, cursor: domainLocked ? 'not-allowed' : 'text' }}
                      placeholder="yourcompany.com"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (domainLocked) {
                          if (window.confirm('Changing your domain can break client access. Are you sure you want to unlock it?')) {
                            setDomainLocked(false);
                          }
                        } else {
                          setDomainLocked(true);
                        }
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors"
                      style={{ color: domainLocked ? 'var(--accent-danger)' : 'var(--accent-success)' }}
                      title={domainLocked ? 'Unlock to edit domain' : 'Lock domain'}
                    >
                      {domainLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </button>
                  </div>
                  {!domainLocked && (
                    <p className="text-[10px] mt-1 font-semibold" style={{ color: 'var(--accent-danger)' }}>
                      ⚠️ Changing domain may break existing client access
                    </p>
                  )}
                </MobileField>

                <MobileField label="Legal Name">
                  <input type="text" value={brokerSettings.legal_name || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, legal_name: e.target.value })}
                    className={inputClass} style={inputStyle} placeholder="Broker LLC" />
                </MobileField>

                <MobileField label="License Number">
                  <input type="text" value={brokerSettings.license_number || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, license_number: e.target.value })}
                    className={inputClass} style={inputStyle} placeholder="FCA-12345" />
                </MobileField>

                {/* Environment — read only */}
                <MobileField label="Environment">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm"
                    style={readonlyStyle}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${brokerSettings.environment === 'live' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                    <span className="font-semibold capitalize">{brokerSettings.environment || 'live'}</span>
                    <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>Read only</span>
                  </div>
                </MobileField>

                {/* Broker Active — read only */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm"
                  style={readonlyStyle}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${brokerSettings.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="font-semibold">{brokerSettings.is_active ? 'Active' : 'Inactive'}</span>
                  <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>Read only</span>
                </div>
              </MobileSection>

              {/* Contact Information */}
              <MobileSection title="Contact Information" icon={Mail}>
                <MobileField label="Support Email">
                  <input type="email" value={brokerSettings.support_email || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, support_email: e.target.value })}
                    className={inputClass} style={inputStyle} placeholder="support@broker.com" />
                </MobileField>
                <MobileField label="Support Phone">
                  <input type="tel" value={brokerSettings.support_phone || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, support_phone: e.target.value })}
                    className={inputClass} style={inputStyle} placeholder="+1 234 567 8900" />
                </MobileField>
                <MobileField label="Address">
                  <textarea value={brokerSettings.address || ''}
                    onChange={e => setBrokerSettings({ ...brokerSettings, address: e.target.value })}
                    className={inputClass} style={inputStyle} rows="3" placeholder="123 Wall Street..." />
                </MobileField>
              </MobileSection>

              {/* Branding */}
              <MobileSection title="Branding" icon={Palette}>
                <MobileField label="Logo">
                  <MediaUploader
                    value={brokerSettings.logo_url || ''}
                    onChange={url => setBrokerSettings({ ...brokerSettings, logo_url: url })}
                    accept="image"
                    label="Logo"
                  />
                </MobileField>
                <MobileField label="Primary Color">
                  <div className="flex gap-2">
                    <input type="color" value={brokerSettings.primary_color || '#1976D2'}
                      onChange={e => setBrokerSettings({ ...brokerSettings, primary_color: e.target.value })}
                      className="w-14 h-10 rounded-xl" />
                    <input type="text" value={brokerSettings.primary_color || '#1976D2'}
                      onChange={e => setBrokerSettings({ ...brokerSettings, primary_color: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-xl border text-xs font-mono focus:outline-none"
                      style={inputStyle} pattern="^#[0-9A-Fa-f]{6}$" />
                  </div>
                </MobileField>
                <MobileField label="Secondary Color">
                  <div className="flex gap-2">
                    <input type="color" value={brokerSettings.secondary_color || '#424242'}
                      onChange={e => setBrokerSettings({ ...brokerSettings, secondary_color: e.target.value })}
                      className="w-14 h-10 rounded-xl" />
                    <input type="text" value={brokerSettings.secondary_color || '#424242'}
                      onChange={e => setBrokerSettings({ ...brokerSettings, secondary_color: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-xl border text-xs font-mono focus:outline-none"
                      style={inputStyle} pattern="^#[0-9A-Fa-f]{6}$" />
                  </div>
                </MobileField>
              </MobileSection>

              {/* Languages */}
              <MobileSection title="Supported Languages" icon={Globe}>
                <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                  Select languages available on the client portal
                </p>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map(lang => (
                    <LangPill
                      key={lang.code}
                      lang={lang}
                      selected={(brokerSettings.supported_languages || []).includes(lang.code)}
                      onToggle={toggleLanguage}
                    />
                  ))}
                </div>
              </MobileSection>

              {/* System Limits */}
              <MobileSection title="System Limits" icon={SettingsIcon}>
                <MobileField label="Max Accounts">
                  <input type="number" value={brokerSettings.max_accounts || 10000}
                    onChange={e => setBrokerSettings({ ...brokerSettings, max_accounts: parseInt(e.target.value) })}
                    className={`${inputClass} font-mono`} style={inputStyle} min="0" />
                </MobileField>
                <MobileField label="Max Concurrent Trades">
                  <input type="number" value={brokerSettings.max_concurrent_trades || 100000}
                    onChange={e => setBrokerSettings({ ...brokerSettings, max_concurrent_trades: parseInt(e.target.value) })}
                    className={`${inputClass} font-mono`} style={inputStyle} min="0" />
                </MobileField>
              </MobileSection>

              {/* Maintenance Mode */}
              <MobileSection title="Maintenance" icon={AlertTriangle}>
                <Toggle
                  checked={Boolean(brokerSettings.maintenance_mode)}
                  onChange={v => setBrokerSettings({ ...brokerSettings, maintenance_mode: v ? 1 : 0 })}
                  label="Maintenance Mode"
                  description="When enabled, clients will see a maintenance message"
                  danger
                />
                {Boolean(brokerSettings.maintenance_mode) && (
                  <MobileField label="Maintenance Message">
                    <textarea
                      value={brokerSettings.maintenance_message && brokerSettings.maintenance_message !== '0' ? brokerSettings.maintenance_message : ''}
                      onChange={e => setBrokerSettings({ ...brokerSettings, maintenance_message: e.target.value })}
                      className={inputClass} style={inputStyle} rows="3"
                      placeholder="We are currently performing scheduled maintenance. Please check back soon." />
                  </MobileField>
                )}
              </MobileSection>

            </div>

            {/* ── Floating save button ────────────────────────────────── */}
            <div className="sticky bottom-0 p-4 border-t"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
              <button onClick={handleSaveBrokerSettings} disabled={saving}
                className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-all"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </>
        ) : null}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold mb-2">Broker Settings</h1>
        <p className="text-[var(--text-secondary)]">Configure your broker information and preferences</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[var(--text-secondary)]">Loading settings...</p>
          </div>
        </div>
      ) : brokerSettings && (
        <div className="space-y-6">

          {/* General Information */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold mb-4">General Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Broker Name *</label>
                <input type="text" value={brokerSettings.name || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  placeholder="My Broker" required />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Domain</label>
                  <button type="button" onClick={() => setShowDomainHelp(true)}
                    className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:underline">
                    <Info className="w-3.5 h-3.5" /> How to connect your domain
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={brokerSettings.domain || ''}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, domain: e.target.value })}
                    readOnly={domainLocked}
                    className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                    style={{ paddingRight: '2.75rem', opacity: domainLocked ? 0.65 : 1, cursor: domainLocked ? 'not-allowed' : 'text' }}
                    placeholder="yourcompany.com"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (domainLocked) {
                        if (window.confirm('Changing your domain can break client access and DNS settings. Are you sure you want to unlock it?')) {
                          setDomainLocked(false);
                        }
                      } else {
                        setDomainLocked(true);
                      }
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors hover:bg-[var(--bg-primary)]"
                    style={{ color: domainLocked ? 'var(--accent-danger)' : 'var(--accent-success)' }}
                    title={domainLocked ? 'Unlock to edit domain' : 'Lock domain'}
                  >
                    {domainLocked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                  </button>
                </div>
                {!domainLocked && (
                  <p className="text-xs mt-1.5 font-semibold" style={{ color: 'var(--accent-danger)' }}>
                    ⚠️ Changing domain may break existing client access and DNS routing
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Legal Name</label>
                <input type="text" value={brokerSettings.legal_name || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, legal_name: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  placeholder="Broker LLC" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">License Number</label>
                <input type="text" value={brokerSettings.license_number || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, license_number: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  placeholder="FCA-12345" />
              </div>

              {/* Environment — read only */}
              <div>
                <label className="block text-sm font-medium mb-2">Environment</label>
                <div className="flex items-center gap-2.5 px-4 py-2 rounded-lg border"
                  style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', opacity: 0.7, cursor: 'not-allowed' }}>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${brokerSettings.environment === 'live' ? 'bg-green-400' : 'bg-yellow-400'}`} />
                  <span className="text-sm font-semibold capitalize" style={{ color: 'var(--text-primary)' }}>{brokerSettings.environment || 'live'}</span>
                  <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>Read only — contact support to change</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Broker Status</label>
                <div className="flex items-center gap-2.5 px-4 py-2 rounded-lg border"
                  style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', opacity: 0.7, cursor: 'not-allowed' }}>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${brokerSettings.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {brokerSettings.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>Read only — contact support to change</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Support Email</label>
                <input type="email" value={brokerSettings.support_email || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, support_email: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  placeholder="support@broker.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Support Phone</label>
                <input type="tel" value={brokerSettings.support_phone || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, support_phone: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  placeholder="+1 234 567 8900" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Address</label>
                <textarea value={brokerSettings.address || ''}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, address: e.target.value })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                  rows="3" placeholder="123 Wall Street, New York, NY 10005" />
              </div>
            </div>
          </div>

          {/* Branding */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold mb-4">Branding</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block text-sm font-medium mb-2">Logo</label>
                <MediaUploader
                  value={brokerSettings.logo_url || ''}
                  onChange={(url) => setBrokerSettings({ ...brokerSettings, logo_url: url })}
                  accept="image"
                  label="Broker Logo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Primary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={brokerSettings.primary_color || '#1976D2'}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, primary_color: e.target.value })}
                    className="w-16 h-10 rounded-lg cursor-pointer" />
                  <input type="text" value={brokerSettings.primary_color || '#1976D2'}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, primary_color: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none font-mono"
                    pattern="^#[0-9A-Fa-f]{6}$" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Secondary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={brokerSettings.secondary_color || '#424242'}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, secondary_color: e.target.value })}
                    className="w-16 h-10 rounded-lg cursor-pointer" />
                  <input type="text" value={brokerSettings.secondary_color || '#424242'}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, secondary_color: e.target.value })}
                    className="flex-1 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none font-mono"
                    pattern="^#[0-9A-Fa-f]{6}$" />
                </div>
              </div>
            </div>
          </div>

          {/* Supported Languages */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="text-lg font-semibold">Supported Languages</h3>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Select which languages are available on the client portal. At least one language must be selected.
            </p>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map(lang => (
                <LangPill
                  key={lang.code}
                  lang={lang}
                  selected={(brokerSettings.supported_languages || []).includes(lang.code)}
                  onToggle={toggleLanguage}
                />
              ))}
            </div>
            {(brokerSettings.supported_languages || []).length > 0 && (
              <p className="text-xs mt-3" style={{ color: 'var(--text-tertiary)' }}>
                Active: {(brokerSettings.supported_languages || []).join(', ').toUpperCase()}
              </p>
            )}
          </div>

          {/* System Limits */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold mb-4">System Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Max Accounts</label>
                <input type="number" value={brokerSettings.max_accounts || 10000}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, max_accounts: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none font-mono"
                  min="0" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Max Concurrent Trades</label>
                <input type="number" value={brokerSettings.max_concurrent_trades || 100000}
                  onChange={(e) => setBrokerSettings({ ...brokerSettings, max_concurrent_trades: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none font-mono"
                  min="0" />
              </div>
            </div>
          </div>

          {/* Maintenance Mode */}
          <div className="p-6 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]"
            style={{ borderColor: brokerSettings.maintenance_mode ? 'var(--accent-danger)' : 'var(--border-color)' }}>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--accent-danger)' }} />
              <h3 className="text-lg font-semibold">Maintenance Mode</h3>
            </div>
            <div className="space-y-4">
              <Toggle
                checked={Boolean(brokerSettings.maintenance_mode)}
                onChange={v => setBrokerSettings({ ...brokerSettings, maintenance_mode: v ? 1 : 0 })}
                label="Enable Maintenance Mode"
                description="When enabled, clients will see the maintenance message instead of the platform"
                danger
              />
              {Boolean(brokerSettings.maintenance_mode) && (
                <div>
                  <label className="block text-sm font-medium mb-2">Maintenance Message</label>
                  <textarea
                    value={brokerSettings.maintenance_message && brokerSettings.maintenance_message !== '0' ? brokerSettings.maintenance_message : ''}
                    onChange={(e) => setBrokerSettings({ ...brokerSettings, maintenance_message: e.target.value })}
                    className="w-full px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg focus:border-[var(--accent-primary)] focus:outline-none transition-smooth"
                    rows="3"
                    placeholder="We are currently performing scheduled maintenance. Please check back soon." />
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button onClick={handleSaveBrokerSettings} disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-primary-hover)] transition-smooth disabled:opacity-50 disabled:cursor-not-allowed">
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {showDomainHelp && (
        <DomainSetupModal
          onClose={() => setShowDomainHelp(false)}
          currentDomain={brokerSettings?.domain}
        />
      )}
    </div>
  );
}